import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import TranslationsContext from 'providers/translationsProvider';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// Dependencies
import { LAYOUT_LIMIT_WIDTH } from 'components/elements/Form';
import { useLoginMutation } from 'services/apiSlices/oauth2Api';
import bfbTheme from 'styles/b4b-theme';
import languageDetector, { LANGUAGE_OPTIONS } from 'utils/languageDetector';
import queryString from 'utils/queryString';

// Actions and Selectors
import { actions as translationActions } from 'store/modules/translations';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// Components
import {
  Button,
  // Checkbox,
  ErrorModal,
  Form,
  Input,
  Link,
  Notification,
  Panel,
  PasswordInput,
  Select,
} from 'components/elements';
import { H1, P } from 'components/fontSystem';
import SecurityText from './ForgotPassword/SecurityStatement';

// Containers
import ForgotPasswordModal from './ForgotPassword';

const INPUT_USER = 'username';
const INPUT_PASS = 'password';
// const REMEMBER_ME = 'rememberuser';
const INPUT_LANGUAGE = 'language';

const { clearError } = authActions;
const { translationsChange } = translationActions;

const LoginForm = ({
  bottomImg = null,
  bottomLayout = LAYOUT_LIMIT_WIDTH,
  logoImg = null,
  logoLayout = LAYOUT_LIMIT_WIDTH,
  onAuth = null,
}) => {
  const { t } = useContext(TranslationsContext);
  const dispatch = useDispatch();

  const [language, setLanguage] = useState(languageDetector());
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [error, setError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);

  const isLoading = useSelector(userSelectors.fetchingUser) || isFetching;
  const isError = useSelector(userSelectors.isError) || error;

  const [login] = useLoginMutation();

  useEffect(() => {
    dispatch(translationsChange({ selectedLanguage: language }));
  }, [language]);

  const getQueryParams = () => queryString.parse(document.location.search);
  const { organization, program } = getQueryParams();
  const loginParams =
    organization && program ? `?organization=${organization}&program=${program}` : '';

  const handleOnSelectLanguage = value => {
    setLanguage(value);
  };

  const handleOnFinish = values => {
    setIsFetching(true);
    login({
      username: values.username.toLowerCase(),
      password: values.password,
    })
      .unwrap()
      .then(data => onAuth && onAuth(data))
      .catch(err => {
        setError(err)
      })
      .finally(() => setIsFetching(false));
  };

  const handleClearError = () => {
    setError(null);
    dispatch(clearError());
  };

  return (
    <Panel>
      <Notification />
      <Form className="white-bg" layout="vertical" onFinish={handleOnFinish}>
        <Form.Header imgSrc={logoImg} imgAlt={t('Program logo')} $logoLayout={logoLayout} />
        <Row type="flex" justify="space-between" style={{ paddingBottom: '15px' }}>
          <Col sm={24} xs={24} md={12}>
            <H1>{t('Welcome back!')}</H1>
          </Col>
          <Col>
            <Form.Item
              size="small"
              initialValue={language}
              name={INPUT_LANGUAGE}
              className="line-height-1"
            >
              <Select
                test-data-id="login-select-languages"
                onSelect={handleOnSelectLanguage}
                options={LANGUAGE_OPTIONS}
                popupMatchSelectWidth={false}
                className="languages-select"
                popupClassName="languages-menu"
                bordered
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name={INPUT_USER}
          size="large"
          label={t('Email')}
          rules={[{ required: true, message: t('Enter your email here') }]}
        >
          <Input type="email" placeholder={t('Your email')} />
        </Form.Item>
        <Form.Item
          name={INPUT_PASS}
          size="large"
          label={t('Password')}
          rules={[{ required: true, message: t('Please input your Password!') }]}
        >
          <PasswordInput placeholder={t('Your password')} name={INPUT_PASS} hidePopover />
        </Form.Item>
        {/* <Form.Item size="large" name={REMEMBER_ME} valuePropName="checked">
          <Checkbox>{t('Remember Me')}</Checkbox>
        </Form.Item> */}
        <ActionsRow type="flex">
          <div className="login-button-container">
            <Form.Item name="submit">
              <Button
                htmlType="submit"
                loading={isLoading}
                size="large"
                style={{ marginTop: '8px' }}
                type="primary"
              >
                {t('LOGIN')}
              </Button>
            </Form.Item>
          </div>
          <div className="login-help-text">
            <P style={{ marginTop: '10px', marginBlockEnd: '0px' }}>
              <span>{`${t("Don't have an account yet? Sign up")} `}</span>
              <Link
                to={{
                  pathname: '/signup',
                  search: loginParams,
                }}
              >
                {t('here')}
              </Link>
              <span>.</span>
            </P>
            <StyledA onClick={() => setResetModalVisible(true)} role="button">
              {t('Forgot password?')}
            </StyledA>
          </div>
        </ActionsRow>

        <SecurityText />

        <ForgotPasswordModal
          visible={resetModalVisible}
          onCancel={() => setResetModalVisible(false)}
        />
        <ErrorModal
          onCancel={handleClearError}
          visible={isError}
          title={t('Oops!')}
          description={t('The credentials are incorrect or the account does not exist.')}
          buttonValue={t('TRY AGAIN')}
        />
        <Form.FooterImg src={bottomImg} $logoLayout={bottomLayout} />
      </Form>
    </Panel>
  );
};

const StyledA = styled.a`
  font-size: 15px;
  text-decoration: underline;
  margin-top: 10px;
`;

const ActionsRow = styled(Row)`
  flex-direction: column;
  .login-button-container {
    flex: 0;
  }
  .login-help-text {
    flex: 1;
  }
  @media screen and (min-width: ${bfbTheme.screenLg}) {
    flex-direction: row;
    .login-help-text {
      margin-left: 20px;
    }
  }
`;

LoginForm.propTypes = {
  bottomImg: PropTypes.string,
  bottomLayout: PropTypes.number,
  logoImg: PropTypes.string,
  logoLayout: PropTypes.number,
  onAuth: PropTypes.func,
};

export default LoginForm;
